export type CreditState = "ok" | "warning" | "reached";

export interface CreditStatus {
  applies: boolean;
  state: CreditState;
  paused: boolean;
  skipReason: string | null;
  creditLimit: number | null;
  limitSource: "plan" | "account" | null;
  planCreditLimit: number | null;
  creditLimitOverride: number | null;
  exposure: number;
  owedTotal: number;
  owedCycles: number;
  currentAmount: number;
  currentConversations: number;
  unitPrice: number;
  ratio: number | null;
  overrideUntil: string | null;
}

export type CreditBanner =
  | { kind: "warning"; percent: number; exposure: number; creditLimit: number }
  | { kind: "paused"; exposure: number; creditLimit: number; owedTotal: number; currentAmount: number; canPay: boolean }
  | { kind: "override"; exposure: number; creditLimit: number; overrideUntil: string };

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function parseCreditStatus(raw: unknown): CreditStatus | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    applies: r.applies === true,
    state: r.state === "warning" || r.state === "reached" ? r.state : "ok",
    paused: r.paused === true,
    skipReason: typeof r.skip_reason === "string" ? r.skip_reason : null,
    creditLimit: numOrNull(r.credit_limit),
    limitSource: r.limit_source === "plan" || r.limit_source === "account" ? r.limit_source : null,
    planCreditLimit: numOrNull(r.plan_credit_limit),
    creditLimitOverride: numOrNull(r.credit_limit_override),
    exposure: num(r.exposure),
    owedTotal: num(r.owed_total),
    owedCycles: num(r.owed_cycles),
    currentAmount: num(r.current_amount),
    currentConversations: num(r.current_conversations),
    unitPrice: num(r.unit_price),
    ratio: numOrNull(r.ratio),
    overrideUntil: typeof r.override_until === "string" ? r.override_until : null,
  };
}

export function creditBanner(status: CreditStatus | null): CreditBanner | null {
  if (!status?.applies || status.creditLimit === null) return null;
  const { exposure, creditLimit } = status;
  if (status.paused) {
    return {
      kind: "paused",
      exposure,
      creditLimit,
      owedTotal: status.owedTotal,
      currentAmount: status.currentAmount,
      canPay: status.owedTotal > 0,
    };
  }
  if (status.state === "reached" && status.overrideUntil) {
    return { kind: "override", exposure, creditLimit, overrideUntil: status.overrideUntil };
  }
  if (status.state === "warning") {
    return { kind: "warning", percent: Math.floor((exposure / creditLimit) * 100), exposure, creditLimit };
  }
  return null;
}

export function formatCop(amount: number): string {
  return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(amount);
}

export function creditLimitSourceLabel(status: CreditStatus): string {
  if (status.skipReason === "billing_exempt") return "No aplica: cuenta exenta de bloqueo por facturación";
  if (status.skipReason === "billing_no_charge") return "No aplica: cuenta sin cobro";
  if (status.skipReason === "not_per_use" || status.skipReason === "no_plan") return "No aplica: solo planes de pago por uso";
  if (status.creditLimit === null) {
    return status.creditLimitOverride === 0 ? "Sin tope (definido para esta cuenta)" : "Sin tope (el plan no tiene)";
  }
  return status.limitSource === "account" ? "Tope propio de esta cuenta" : "Tope del plan";
}

export type OverrideMode = "plan" | "none" | "custom";

export function overrideModeOf(override: number | null): OverrideMode {
  if (override === null) return "plan";
  return override === 0 ? "none" : "custom";
}

export function overridePayload(mode: OverrideMode, amount: string): number | null | "invalid" {
  if (mode === "plan") return null;
  if (mode === "none") return 0;
  const value = Number(amount.replace(/[.\s]/g, ""));
  return Number.isInteger(value) && value > 0 ? value : "invalid";
}
